import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { useCareerJourney } from 'core';
import { careerColor } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

import { CareerNextSteps } from './parts/CareerNextSteps';
import { CareerPlanAction } from './parts/CareerPlanAction';
import { CareerPlanGoal } from './parts/CareerPlanGoal';
import { CareerPlanSkeleton } from './parts/CareerPlanSkeleton';
import { CareerRoute } from './parts/CareerRoute';

/**
 * `/career` — «Mój plan kariery» (ТЗ 0071, п. 4).
 *
 * Порядок блоков — ответ на три вопроса, которые студент должен найти за
 * 30 секунд: какая у меня цель → что сделать сейчас → где я на пути.
 *
 *   1. заголовок
 *   2. цель или предложение её выбрать
 *   3. ОДНО основное действие
 *   4. маршрут из четырёх этапов, текущий раскрыт
 *   5. «Możesz też» — не больше двух дополнительных действий
 *
 * «Визуальное упрощение» (15.09): колонка до 1200 px от левого края; разделы
 * через 32, от заголовка до цели — 24; насыщенный жёлтый — только у кнопки
 * основного действия.
 *
 * Сняты «Ukończone kroki 13/20», «Gotowość do aplikowania» и сводка откликов:
 * три представления тех же чисел, что уже стоят в этапах маршрута.
 *
 * 🔴 Ни одного приоритета экран не выбирает и ни одного числа не считает —
 * всё приходит с сервера одним запросом.
 */
export const CareerJourney = memo(() => {
  const { t } = useTranslation();
  const { journey, error, refetch } = useCareerJourney();

  if (error && !journey) {
    return (
      <ErrorBox role="alert">
        <p>{t('careerCenter.plan.loadError')}</p>
        <RetryButton type="button" onClick={() => refetch()}>
          {t('careerCenter.plan.retry')}
        </RetryButton>
      </ErrorBox>
    );
  }

  if (!journey) {
    return <CareerPlanSkeleton />;
  }

  const primary = journey.primaryAction;
  // Цели нет — блок выбора цели И ЕСТЬ основное действие. Вторая такая же
  // карточка под ним — две одинаковые кнопки вместо одного акцента.
  const showPrimary = primary !== null && !(primary.kind === 'goal' && !journey.hasGoal);

  return (
    <Wrapper>
      <Header>
        <PageTitle>{t('careerCenter.plan.title')}</PageTitle>
        <Lead>{t('careerCenter.plan.lead')}</Lead>
      </Header>

      <CareerPlanGoal goal={journey.goal} hasGoal={journey.hasGoal} />

      {showPrimary && primary ? <CareerPlanAction action={primary} /> : null}

      <CareerRoute sections={journey.sections} goal={journey.goal} hasGoal={journey.hasGoal} />

      <CareerNextSteps actions={journey.secondaryActions} />
    </Wrapper>
  );
});

const Wrapper = styled.div`
  /* width: 100% обязателен: шаблон — flex-колонка с align-items: flex-start,
     и без ширины страница схлопывается по содержимому. */
  width: 100%;
  max-width: ${careerLayout.planWidth}px;
  display: flex;
  flex-direction: column;
  gap: ${careerLayout.sectionGap}px;
  padding-top: 8px;
  color: ${careerColor.text};
`;

const Header = styled.header`
  /* До блока цели — 24, а не 32 общего шага разделов. */
  margin-bottom: -8px;
`;

const PageTitle = styled.h1`
  margin: 0;
  color: ${careerColor.text};
  ${careerType.h1()};
`;

const Lead = styled.p`
  margin: 8px 0 0;
  color: ${careerColor.text2};
  ${careerType.body()};
`;

const ErrorBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  width: 100%;
  max-width: ${careerLayout.planWidth}px;
  margin-top: 8px;
  padding: 16px;
  border-radius: ${careerLayout.cardRadius}px;
  border: 1px solid ${careerColor.line};
  ${careerType.body()};

  p {
    margin: 0;
  }
`;

const RetryButton = styled.button`
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 16px;
  border: 1px solid ${careerColor.text};
  border-radius: ${careerLayout.controlRadius}px;
  background: transparent;
  color: ${careerColor.text};
  cursor: pointer;
  ${careerType.bodyStrong()};
`;
